import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, DownloadSimple, Wrench, ArrowLeft } from "@phosphor-icons/react";
import { C } from "@/components/fm/shared";

export const David = ({ children }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-full grid place-items-center shrink-0 font-extrabold text-sm"
      style={{ backgroundColor: C.teal, color: "#fff" }}>D</div>
    <div className="rounded-2xl rounded-tl-sm px-5 py-4 max-w-2xl"
      style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.15)" }}>
      {children}
    </div>
  </div>
);

const YEARS5 = ["f1", "f2", "f3", "f4", "f5"];
const yLabel = (y, labels) => labels?.[YEARS5.indexOf(y)] || `Year ${YEARS5.indexOf(y) + 1}`;
const pctOf = (v) => Math.round((Number(v) || 0) * 100);
const compact = (v) => {
  const a = Math.abs(Number(v) || 0);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${Math.round(v)}`;
};

/* ── Section 8 · scenario stress chart (annual revenue, 3 futures) ── */
function ScenarioChart({ scenarios, yearLabels }) {
  if (!scenarios) return null;
  const series = [
    ["downside", "Downside", "#F87171"],
    ["base", "Base", C.tealSoft],
    ["upside", "Upside", C.green],
  ].map(([key, label, color]) => ({
    key, label, color,
    vals: YEARS5.map((y) => Number(((scenarios[key] || {}).annual || {})[y]?.revenue) || 0),
  }));
  const max = Math.max(1, ...series.flatMap((s) => s.vals));
  const X0 = 44, XW = 118, Y0 = 150, YH = 120;
  const x = (i) => X0 + i * XW;
  const y = (v) => Y0 - (v / max) * YH;
  return (
    <div className="rounded-xl p-4 border mt-3" data-testid="fm-scenario-chart"
      style={{ backgroundColor: C.panel, borderColor: "rgba(123,211,206,0.12)" }}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: C.tealSoft }}>
          Annual revenue — three futures (KES)
        </div>
        <div className="flex items-center gap-3">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-white/60">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: s.color }} />
              {s.label} · {compact(s.vals[4])}
            </span>
          ))}
        </div>
      </div>
      <svg viewBox="0 0 600 170" className="w-full" style={{ maxHeight: 180 }}>
        {[0, 0.5, 1].map((t) => (
          <line key={t} x1={X0 - 6} x2={x(4) + 6} y1={Y0 - YH * t} y2={Y0 - YH * t}
            stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
        ))}
        {YEARS5.map((yr, i) => (
          <text key={yr} x={x(i)} y={166} textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.4)">
            {yearLabels?.[i] || `Y${i + 1}`}
          </text>
        ))}
        {series.map((s) => (
          <g key={s.key}>
            <polyline fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
              points={s.vals.map((v, i) => `${x(i)},${y(v)}`).join(" ")} />
            <circle cx={x(4)} cy={y(s.vals[4])} r="3.5" fill={s.color} />
          </g>
        ))}
      </svg>
      <div className="text-[10px] text-white/35 mt-1">
        Built from your saved deltas — it refreshes after you continue with any changes.
      </div>
    </div>
  );
}

/* ── Section 8 · three scenario cards side by side ─────────────── */
const DELTA_FIELDS = [["volume_delta", "Volume"], ["price_delta", "Price"], ["cogs_delta", "COGS"]];

export function ScenarioCards({ d, setDraft, scenarios, yearLabels }) {
  const edit = (scn, k, val) => setDraft({ ...d, [scn]: { ...d[scn], [k]: (Number(val) || 0) / 100 } });
  const card = (name, title, sub, editable, extra) => (
    <div className="rounded-xl p-4 border flex flex-col gap-3" data-testid={`fm-scn-card-${name}`}
      style={{ backgroundColor: C.panel, borderColor: name === "base" ? "rgba(123,211,206,0.4)" : "rgba(255,255,255,0.1)" }}>
      <div>
        <div className="text-sm font-extrabold text-white">{title}</div>
        <div className="text-[11px] text-white/45 mt-0.5">{sub}</div>
      </div>
      {DELTA_FIELDS.map(([k, label]) => (
        <div key={k} className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] uppercase tracking-wider text-white/45 font-semibold">{label} change %</span>
          {editable ? (
            <input type="number" className="w-20 px-2 py-1.5 rounded-md text-sm text-white text-right focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)" }}
              value={pctOf(d?.[name]?.[k])} data-testid={`fm-scn-${name}-${k}`}
              onChange={(e) => edit(name, k, e.target.value)} />
          ) : (
            <span className="text-sm font-bold text-white/70" data-testid={`fm-scn-base-${k}`}>0%</span>
          )}
        </div>
      ))}
      {extra && <div className="text-[10.5px] leading-relaxed mt-auto" style={{ color: C.orange }}>{extra}</div>}
    </div>
  );
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="fm-scenario-cards">
        {card("downside", "Downside", "If things go worse than planned", true,
          "A cost floor applies — costs never go below your fixed overheads, regardless of the revenue drop.")}
        {card("base", "Base", "Exactly as entered in the Q&A", false, null)}
        {card("upside", "Upside", "If things go better than planned", true, null)}
      </div>
      <ScenarioChart scenarios={scenarios} yearLabels={yearLabels} />
    </div>
  );
}

/* ── Section 9 · DSCR verdict (computed, no questions) ─────────── */
export function DscrVerdict({ dscr, onGoSection6, yearLabels }) {
  if (!dscr) return <div className="text-sm text-white/45" data-testid="fm-dscr-loading">Computing your debt service coverage…</div>;
  const ys = dscr.years || {};
  const target = dscr.target_dscr || 1.25;
  const hasDebt = YEARS5.some((y) => ((ys[y] || {}).debt_service || 0) > 0.01);
  const bad = YEARS5.filter((y) => (ys[y] || {}).dscr != null && ys[y].dscr < target);
  let verdict, kind;
  if (!hasDebt) {
    kind = "nodebt";
    verdict = "You have no existing debt in this model. If you plan to raise via debt, go back to Section 6 and enter your expected loan terms — I'll recompute your DSCR automatically.";
  } else if (!bad.length) {
    kind = "healthy";
    verdict = "Your debt service coverage ratio is healthy. This means your business can comfortably service commercial debt. A bank or DFI would likely consider you for a loan.";
  } else {
    kind = "low";
    const lastBad = YEARS5.indexOf(bad[bad.length - 1]);
    const improve = YEARS5.slice(lastBad + 1).find((y) => (ys[y] || {}).dscr == null || ys[y].dscr >= target);
    verdict = `Your debt service coverage ratio is below 1.25x in ${bad.map((y) => yLabel(y, yearLabels)).join(" and ")}. This means commercial debt is not yet viable for those years. I'd recommend focusing on equity or revenue-based financing for now.`;
    if (improve) verdict += ` Your model shows this improves in ${yLabel(improve, yearLabels)} — that is when debt becomes an option.`;
  }
  const tone = kind === "healthy" ? C.green : kind === "low" ? C.orange : C.tealSoft;
  return (
    <div className="space-y-4">
      <div className="rounded-xl p-5 border" data-testid={`fm-dscr-verdict-${kind}`}
        style={{ backgroundColor: C.panel, borderColor: `${tone}66` }}>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-2" style={{ color: tone }}>
          DSCR — Debt Service Coverage
        </div>
        <div className="text-sm text-white/85 leading-relaxed" data-testid="fm-dscr-verdict-text">{verdict}</div>
        {kind === "nodebt" && (
          <button onClick={onGoSection6} data-testid="fm-dscr-go-section6"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
            style={{ backgroundColor: "rgba(10,139,133,0.18)", color: C.tealSoft, border: "1px solid rgba(10,139,133,0.4)" }}>
            <ArrowLeft size={12} /> Go back to Section 6 — add loan terms
          </button>
        )}
      </div>
      {hasDebt && (
        <div className="rounded-xl border overflow-hidden" data-testid="fm-dscr-table"
          style={{ backgroundColor: C.panel, borderColor: "rgba(123,211,206,0.12)" }}>
          <div className="grid grid-cols-4 px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-white/40 border-b border-white/5">
            <span>Year</span><span className="text-right">Debt service</span><span className="text-right">DSCR</span><span className="text-right">Status</span>
          </div>
          {YEARS5.map((y) => {
            const row = ys[y] || {};
            const col = row.dscr == null ? "rgba(255,255,255,0.35)" : row.dscr >= target ? C.green : C.red;
            return (
              <div key={y} className="grid grid-cols-4 px-4 py-2 text-sm border-b border-white/5 last:border-0">
                <span className="text-white/60 font-semibold">{yLabel(y, yearLabels)}</span>
                <span className="text-right text-white/80">{row.debt_service ? `KES ${Number(row.debt_service).toLocaleString("en-KE")}` : "—"}</span>
                <span className="text-right font-bold" style={{ color: col }}>{row.dscr != null ? `${row.dscr.toFixed(2)}×` : "—"}</span>
                <span className="text-right text-[11px] font-bold uppercase" style={{ color: col }}>
                  {row.dscr == null ? "no service" : row.dscr >= target ? "healthy" : "below 1.25×"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Section 10 · CRS scorecard + Excel download ───────────────── */
const CRS_FIX = {
  profile: { sec: 1, fix: "Complete your business profile — company name, sector, stage and funder type." },
  revenue_drivers: { sec: 3, fix: "Enter the volume and price for your selected revenue model." },
  cost_structure: { sec: 4, fix: "Set your COGS % and add at least one operating cost line." },
  formula_integrity: { sec: null, fix: "Blocking check — all outputs must stay formula-derived." },
  balance: { sec: 6, fix: "Re-check opening cash, assets, loans and raises so Assets = Liabilities + Equity." },
  cash: { sec: 6, fix: "Closing cash goes negative — add funding, reduce costs, or shift spending later." },
  growth: { sec: 3, fix: "Revenue CAGR exceeds 200% per year — reduce your growth rate or be ready to justify it." },
  history: { sec: 3, fix: "Mark the milestone that ignites revenue (pre-revenue), or enter your last 3 years of actuals." },
};

export function CrsFinal({ crs, model, breakeven, onFix, onOpenModel }) {
  const [cost, setCost] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const score = crs?.score;
  const failing = (crs?.checks || []).filter((c) => !c.passed);
  const headline = crs == null ? "Crunching your final score…"
    : score >= 80 ? "Your model is funder-ready. Download your Excel file below."
      : score >= 60 ? "You're close. Here is what is holding your score back:"
        : "Your model needs more work before it is funder-ready. Fix these items first:";

  const doDownload = async () => {
    setConfirming(false); setBusy(true);
    try {
      const { data } = await api.get(`/fm/models/${model.fm_id}/export/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${model.name || "Financial model"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      window.dispatchEvent(new Event("clinq:credits-changed"));
      setDownloaded(true);
    } catch (e) {
      // 402 credit shortage is toasted globally (api.js) with a Buy credits CTA.
      if (e?.response?.status !== 402) toast.error("Export failed — try again");
    }
    setBusy(false);
  };

  const startDownload = async () => {
    try {
      const { data } = await api.get(`/fm/models/${model.fm_id}/export/cost`);
      setCost(data);
      if (data.mode === "free") return doDownload();
      setConfirming(true);
    } catch { toast.error("Could not check the export price — try again"); }
  };

  return (
    <div className="space-y-6" data-testid="fm-interview-final">
      <David>
        <div className="text-lg font-extrabold text-white leading-snug" data-testid="fm-crs-headline">{headline}</div>
        {crs && score < 80 && failing.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {failing.map((c) => (
              <li key={c.id} className="text-[13px] text-white/70 leading-relaxed">
                <span className="font-bold text-white">{c.label}</span> — {(CRS_FIX[c.id] || {}).fix}
              </li>
            ))}
          </ul>
        )}
      </David>

      {crs && (
        <div className="flex flex-wrap gap-4 items-start">
          <div className="flex flex-col gap-4 w-56 shrink-0">
            <div className="rounded-xl p-6 border" data-testid="fm-final-crs"
              style={{ backgroundColor: C.panel, borderColor: "rgba(123,211,206,0.2)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.tealSoft }}>Capital Readiness Score</div>
              <div className="text-5xl font-extrabold mt-2" style={{ color: score >= 80 ? C.green : score >= 60 ? C.orange : C.red }}>
                {score}<span className="text-lg text-white/40">/100</span>
              </div>
              <div className="text-sm text-white/70 mt-1">{crs.band}</div>
              <div className="text-[11px] text-white/40 mt-2">{crs.passed}/{crs.total} checks passed</div>
            </div>
            {breakeven && (
              <div className="rounded-xl p-5 border" data-testid="fm-breakeven-flag"
                style={{ backgroundColor: C.panel, borderColor: breakeven.month ? "rgba(74,222,128,0.3)" : "rgba(245,158,11,0.35)" }}>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.tealSoft }}>Break-even</div>
                {breakeven.month ? (
                  <>
                    <div className="text-2xl font-extrabold mt-2" style={{ color: C.green }} data-testid="fm-breakeven-label">{breakeven.label}</div>
                    <div className="text-[11.5px] text-white/55 mt-1.5 leading-relaxed">
                      Your model first turns profitable in month {breakeven.month} of 60.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-extrabold mt-2" style={{ color: C.orange }} data-testid="fm-breakeven-label">Not yet</div>
                    <div className="text-[11.5px] text-white/55 mt-1.5 leading-relaxed">
                      EBITDA stays negative across the 5-year forecast — funders will ask about your path to profitability.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl border overflow-hidden flex-1 min-w-[320px]" data-testid="fm-crs-scorecard"
            style={{ backgroundColor: C.panel, borderColor: "rgba(123,211,206,0.12)" }}>
            {(crs.checks || []).map((c, i) => {
              const map = CRS_FIX[c.id] || {};
              return (
                <div key={c.id} className={`px-4 py-2.5 ${i > 0 ? "border-t border-white/5" : ""}`} data-testid={`fm-crs-check-${c.id}`}>
                  <div className="flex items-center gap-2.5">
                    {c.passed
                      ? <CheckCircle size={16} weight="fill" style={{ color: C.green, flexShrink: 0 }} />
                      : <XCircle size={16} weight="fill" style={{ color: C.red, flexShrink: 0 }} />}
                    <span className="text-[13px] font-semibold flex-1" style={{ color: c.passed ? "rgba(255,255,255,0.85)" : "#fff" }}>{c.label}</span>
                    {!c.passed && map.sec && (
                      <button onClick={() => onFix(map.sec)} data-testid={`fm-crs-fix-${c.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md shrink-0"
                        style={{ backgroundColor: "rgba(245,158,11,0.14)", color: C.orange, border: "1px solid rgba(245,158,11,0.35)" }}>
                        <Wrench size={11} /> Fix and rescore
                      </button>
                    )}
                  </div>
                  {!c.passed && <div className="text-[11.5px] text-white/45 mt-1 ml-[26px]">{(CRS_FIX[c.id] || {}).fix}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Download — always available regardless of score */}
      <div className="rounded-xl p-5 border max-w-2xl" data-testid="fm-download-panel"
        style={{ backgroundColor: "rgba(10,139,133,0.07)", borderColor: "rgba(10,139,133,0.35)" }}>
        {confirming ? (
          <div data-testid="fm-download-confirm">
            <div className="text-sm font-extrabold text-white mb-1.5">
              Use {cost?.mode === "refresh" ? cost?.refresh_price : cost?.price} credits?
            </div>
            <div className="text-[12px] text-white/60 leading-relaxed mb-3">
              {cost?.mode === "refresh"
                ? `A new-month refresh costs ${cost?.refresh_price} credits — then re-downloads are free for the rest of the month.`
                : "This unlocks your Excel model with free re-downloads for the rest of the month."}
            </div>
            <div className="flex gap-2">
              <button onClick={doDownload} data-testid="fm-download-confirm-btn"
                className="px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: C.orange, color: "#000" }}>Confirm &amp; download</button>
              <button onClick={() => setConfirming(false)} data-testid="fm-download-cancel-btn"
                className="px-4 py-2 rounded-md text-xs font-bold text-white/60 hover:text-white"
                style={{ border: "1px solid rgba(255,255,255,0.15)" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={startDownload} disabled={busy} data-testid="fm-final-download"
              className="px-6 py-3.5 rounded-lg font-extrabold text-sm inline-flex items-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <DownloadSimple size={17} weight="bold" /> {busy ? "Preparing your model…" : "Download Financial Model"}
            </button>
            <button onClick={onOpenModel} data-testid="fm-final-open"
              className="px-5 py-3 rounded-lg font-bold text-sm text-white/80 hover:text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
              Open the full model
            </button>
            {crs && score < 80 && (
              <span className="text-[11px] text-white/45 basis-full">
                You can still download below 80 — I've flagged the issues above so you know what funders will question.
              </span>
            )}
          </div>
        )}
      </div>

      {downloaded && (
        <David>
          <div className="text-sm text-white/85 leading-relaxed" data-testid="fm-download-done-msg">
            Your financial model is downloaded. If you want to update any assumptions, come back and I'll rebuild it for you.
          </div>
        </David>
      )}
    </div>
  );
}
